import {get, snakeCase} from 'lodash';
import {Dispatch} from '@packages/systems/flux';
import type {SelectionRecordType} from '@packages/shared/records';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {createSelector} from 'reselect';
import {ContentEditorActive} from '@packages/shared/states';
import {generateGuid} from '@packages/utilities/guid';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {aiFieldGenerationRequested} from './actions';
import {assertRangeSelectionRecordType} from '@packages/systems/ContentEditor';
import {AI_CONTENT_GENERATION_ENTRY} from '@packages/systems/ai/writingAssistant';
import {AI_WRITE_QUICK_MOD_PROMPTS} from '@packages/systems/ai/shared';

type AiData = ContentEditorActive['aiData'];

export const selectContentEditorPendingRequestState = createSelector(
  (state: DesignerStores) =>
    state.ContentEditorStore.aiData.subtreeForAiInsertion,
  (state: DesignerStores) =>
    state.ContentEditorStore.aiData.selectionForAiInsertion,
  (state: DesignerStores) =>
    state.ContentEditorStore.aiData.isPendingRequestStartFromLlm,
  (state: DesignerStores) => state.ContentEditorStore.aiData.outputFormat,
  (state: DesignerStores) => state.ContentEditorStore.aiData.userPrompt,
  (state: DesignerStores) =>
    state.ContentEditorStore.aiData.htmlSelectionContext,
  (state: DesignerStores) => state.ContentEditorStore.aiData.generationEntry,
  (
    subtreeForAiInsertion,
    selectionForAiInsertion,
    isPendingRequestStartFromLlm,
    outputFormat,
    userPrompt,
    htmlSelectionContext,
    generationEntry
  ) => ({
    subtreeForAiInsertion,
    selectionForAiInsertion,
    isPendingRequestStartFromLlm,
    outputFormat,
    userPrompt,
    htmlSelectionContext,
    generationEntry,
  })
);

let prevIsPendingRequestStartFromLlm: AiData['isPendingRequestStartFromLlm'] = false;

type RunEngineProps = {
  subtreeForAiInsertion: ExpressionElement | null;
  selectionForAiInsertion: SelectionRecordType | null;
  userPrompt: string;
  isPendingRequestStartFromLlm: boolean;
  outputFormat: 'PlainText' | 'RichText' | null;
  htmlSelectionContext: string | null;
  generationEntry?: AI_CONTENT_GENERATION_ENTRY;
};

export const ContentEditorAiRequestEngine = (dispatch: Dispatch) => {
  return ({
    subtreeForAiInsertion,
    selectionForAiInsertion,
    userPrompt,
    htmlSelectionContext,
    isPendingRequestStartFromLlm,
    outputFormat,
    generationEntry,
  }: RunEngineProps) => {
    if (
      prevIsPendingRequestStartFromLlm === false &&
      isPendingRequestStartFromLlm === true
    ) {
      if (subtreeForAiInsertion === null || selectionForAiInsertion === null) {
        return;
      }

      const rangeSelection = assertRangeSelectionRecordType(
        selectionForAiInsertion
      );

      if (rangeSelection === null) {
        console.error(
          'ContentEditorAiRequestEngine: no implementation for node selections'
        );
        return;
      }

      if (!prevIsPendingRequestStartFromLlm && isPendingRequestStartFromLlm) {
        const requestId = generateGuid();
        const seedId = generateGuid();

        if (htmlSelectionContext === null) {
          return;
        }

        if (outputFormat === null) {
          if (process.env.NODE_ENV === 'development') {
            console.error('ContentEditorAiRequestEngine: outputFormat is null');
          }
          return;
        }

        const transformedPrompt = snakeCase(userPrompt).toUpperCase();
        const isQuickModPrompt = Boolean(
          get(AI_WRITE_QUICK_MOD_PROMPTS, transformedPrompt, null)
        );

        dispatch(
          aiFieldGenerationRequested({
            requestId,
            seedId,
            prompt: userPrompt,
            content: htmlSelectionContext,
            outputFormat,
            generationEntry: generationEntry ?? null,
            isQuickModPrompt,
          })
        );
      }
    }

    prevIsPendingRequestStartFromLlm = isPendingRequestStartFromLlm;
  };
};
